/**
 * Rueckmeldungen der Kanzlei, fuer H&K (Rolle verwaltung).
 *
 * Aufgebaut wie der Eingang: eine Zeile je Analyse und Person, neueste
 * zuerst. Ein Klick oeffnet die gewohnte Ansicht mit Beleg und Analyse, so
 * wie die Person sie beim Schreiben sah, daneben ihre Rueckmeldungen
 * (seiten/Rueckmeldung.tsx).
 */
import { useState } from 'react'
import { Link, Navigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAnmeldung } from '../lib/anmeldung'

interface Rueckmeldung {
  id: string
  erledigt_am: string | null
  erstellt_am: string
  verfasser_id: string
  verfasser: string
  dokument_id: string
  bezeichnung: string
  dateiname: string
  mandant_name: string
  mandantennummer: number
}

interface Zeile {
  dokumentId: string
  verfasserId: string
  verfasser: string
  bezeichnung: string
  dateiname: string
  mandantName: string
  mandantennummer: number
  anzahl: number
  offen: number
  zuletzt: string
}

type Filter = 'offen' | 'erledigt' | 'alle'

const spalten = { gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 0.9fr) 130px 100px' }

/** Eine Zeile je Analyse und Person. Die Liste kommt neueste zuerst, also auch die Zeilen. */
function zeilen(liste: Rueckmeldung[]): Zeile[] {
  const je = new Map<string, Zeile>()
  for (const r of liste) {
    const schluessel = `${r.dokument_id}:${r.verfasser_id}`
    const z = je.get(schluessel) ?? {
      dokumentId: r.dokument_id,
      verfasserId: r.verfasser_id,
      verfasser: r.verfasser,
      bezeichnung: r.bezeichnung,
      dateiname: r.dateiname,
      mandantName: r.mandant_name,
      mandantennummer: r.mandantennummer,
      anzahl: 0,
      offen: 0,
      zuletzt: r.erstellt_am,
    }
    z.anzahl++
    if (!r.erledigt_am) z.offen++
    je.set(schluessel, z)
  }
  return [...je.values()]
}

export function RueckmeldungenSeite() {
  const { werkzeug } = useParams()
  const { auskunft } = useAnmeldung()
  const [filter, setFilter] = useState<Filter>('offen')

  const verwaltung = auskunft?.benutzer.rolle === 'verwaltung'

  const abfrage = useQuery({
    queryKey: ['rueckmeldungen', 'alle', werkzeug, filter],
    queryFn: () => api.holen<{ rueckmeldungen: Rueckmeldung[] }>(`/api/rueckmeldungen?werkzeug=${werkzeug}&stand=${filter}`),
    enabled: verwaltung,
  })

  if (!verwaltung) return <Navigate to="/" replace />

  const liste = zeilen(abfrage.data?.rueckmeldungen ?? [])

  return (
    <>
      <div className="filterleiste">
        <span className="leiser">{abfrage.data ? `${liste.length} ${liste.length === 1 ? 'Analyse' : 'Analysen'} mit Rückmeldung` : ''}</span>
        <div className="wahl" role="group" aria-label="Stand">
          {(['offen', 'erledigt', 'alle'] as const).map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {f === 'offen' ? 'Offen' : f === 'erledigt' ? 'Erledigt' : 'Alle'}
            </button>
          ))}
        </div>
      </div>

      <div className="liste">
        <div className="listenkopf" style={spalten}>
          <div>Auswertung</div>
          <div>Mandant</div>
          <div>Von</div>
          <div>Zuletzt</div>
          <div>Stand</div>
        </div>

        {abfrage.isPending && <div className="laedt">Wird geladen …</div>}

        {abfrage.error && (
          <div className="leer">
            <strong>Die Liste konnte nicht geladen werden.</strong>
            {abfrage.error instanceof Error ? abfrage.error.message : ''}
          </div>
        )}

        {abfrage.data && liste.length === 0 && (
          <div className="leer">
            <strong>{filter === 'offen' ? 'Nichts Offenes.' : 'Keine Rückmeldungen.'}</strong>
            <p>Rückmeldungen schreibt die Kanzlei unten rechts in einer geöffneten Analyse. Sie erscheinen hier, sobald sie gesendet sind.</p>
          </div>
        )}

        {liste.map((z) => (
          <Link key={`${z.dokumentId}:${z.verfasserId}`} to={`/${werkzeug}/rueckmeldungen/${z.dokumentId}/${z.verfasserId}`} className="zeile" style={spalten}>
            <div>
              <div className="sache">{z.bezeichnung}</div>
              <div className="nebensache">{z.dateiname}</div>
            </div>
            <div>
              <div className="leise">{z.mandantName}</div>
              <div className="nebensache zahl">{z.mandantennummer}</div>
            </div>
            <div>
              <div className="leise">{z.verfasser}</div>
              <div className="nebensache">{z.anzahl === 1 ? '1 Rückmeldung' : `${z.anzahl} Rückmeldungen`}</div>
            </div>
            <div className="leiser zahl">
              {new Date(z.zuletzt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </div>
            <div>
              <span className={`stand ${z.offen ? 'offen' : 'erledigt'}`}>{z.offen ? 'offen' : 'erledigt'}</span>
            </div>
          </Link>
        ))}
      </div>
    </>
  )
}
